import React, { useCallback, useMemo } from 'react';

import { AttentionMessage } from '@eika/attention-message';
import { CheckboxPanels } from '@eika/checkbox';
import { LabelProps } from '@eika/label';

import type { EFakturaCreditor, EFakturaCreditorId, EFakturaPermissionId } from '../../../../generated-types';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import useFormContext from '../../form/useFormContext';
import formatOrgNumber from '../../format/formatOrgNumber';
import CreditorPanelsSkeleton from './CreditorPanelsSkeleton';
import getLabelProps from './getLabelProps';

import './CreditorPanels.scss';

type Props = {
    selected?: EFakturaCreditor[];
    onChange: (creditor: EFakturaCreditor[]) => void;
    setValidationMessage?: (value: string | undefined) => void;
    validationError?: string;
    userToBeUpdated?: EFakturaPermissionId;
    locked?: boolean;
};

const findCreditor = (creditor1: EFakturaCreditor, creditor2: EFakturaCreditor) =>
    creditor1.creditorId.value === creditor2.creditorId.value;

const CreditorPanels: React.FC<Props> = ({
    selected,
    onChange,
    setValidationMessage,
    validationError,
    userToBeUpdated,
    locked = false,
}) => {
    const translated = useTranslation();
    const {
        creditors: { data: creditorData, isLoading },
        givenEfakturas,
    } = useFormContext();

    const selectCreditorInList = useCallback(
        (creditor: EFakturaCreditor) => {
            setValidationMessage?.(undefined);
            if (selected?.some((selectedCreditor) => findCreditor(selectedCreditor, creditor))) {
                const newList = selected?.filter((selectedCreditor) => !findCreditor(selectedCreditor, creditor));
                onChange(newList);
                return;
            }

            const newList = [...(selected ?? []), creditor];
            onChange(newList);
        },
        [selected, onChange, setValidationMessage],
    );

    const getLabels = useCallback(
        (creditorId: EFakturaCreditorId): LabelProps[] | undefined =>
            getLabelProps(translated)(givenEfakturas.data, creditorId, userToBeUpdated),
        [givenEfakturas, userToBeUpdated, translated],
    );

    const creditorList = useMemo(
        () =>
            creditorData.map((creditor) => ({
                label: creditor.name,
                checked: selected?.some((selectedCreditor) => findCreditor(selectedCreditor, creditor)) ?? false,
                onValueChange: () => selectCreditorInList(creditor),
                description: format(translated.eFakturaSharing.orgNumber, formatOrgNumber(creditor.creditorId.value)),
                value: creditor.creditorId.value,
                labels: getLabels(creditor.creditorId),
                disabled: locked,
            })),
        [translated.eFakturaSharing.orgNumber, creditorData, selected, selectCreditorInList, getLabels, locked],
    );

    return isLoading ? (
        <CreditorPanelsSkeleton count={creditorData.length} />
    ) : (
        <>
            <CheckboxPanels
                label={translated.eFakturaSharing.chooseEfakturasToShare}
                sublabel={translated.eFakturaSharing.chooseFromCompanies}
                options={creditorList}
            />
            {validationError && (
                <AttentionMessage className="creditor-panel-error" type="error" text={validationError} />
            )}
        </>
    );
};

export default CreditorPanels;
